import React, { useEffect, useState } from "react";
import { DashboardEditorProps } from "components/ComponentType";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import {
  defaultDatasetLineChartConfig,
  defaultLoadConf,
} from "components/dashboard/data-input-output-table/defaultConfigs";
import { DatasetChartProps, DatasetLoadingConf } from "./interfaces";
import LoadConfigInputs from "./LoadConfigInputs";
import BarsIdentifiersEditor from "./BarsIdentifiersEditor";

function CommonChartEditor({ config, onConfigChange }: DashboardEditorProps<DatasetChartProps>) {
  let conf = config ?? defaultDatasetLineChartConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultDatasetLineChartConfig;
  }

  const [currentConfig, setCurrentConfig] = useState(conf);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [lineEditorDialogVisible, setLineEditorDialogVisibility] = useState<boolean>(false);

  useEffect(() => {
    const initialSelectedDatasets = currentConfig.datasets?.map(dataset => dataset.identifier);
    setSelectedDatasets(initialSelectedDatasets ?? []);
  }, []);

  const handleConfigChange = (e: InputNumberValueChangeParams | InputSwitchChangeParams): void => {
    setCurrentConfig(prev => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleDatasetChange = (selectedIdentifiers: string[]): void => {
    const selectedDatasetsArray = selectedIdentifiers.map(identifier => {
      const oldDataset = currentConfig.datasets
        ? currentConfig.datasets?.find(dataset => dataset.identifier === identifier)
        : undefined;
      if (oldDataset) {
        return oldDataset;
      }
      return {
        identifier,
      };
    });

    setCurrentConfig(prev => ({ ...prev, datasets: selectedDatasetsArray }));
    setSelectedDatasets(selectedIdentifiers);
  };

  const handleLoadConf = (e: InputSwitchChangeParams): void => {
    const tempConfig = structuredClone(currentConfig);
    if (e.value) {
      tempConfig.loadConf = defaultLoadConf;
    } else {
      delete tempConfig.loadConf;
    }
    setCurrentConfig(tempConfig);
  };

  const handleLoadConfChange = (updatedLoadConf: DatasetLoadingConf) => {
    setCurrentConfig(prev => ({
      ...prev,
      loadConf: updatedLoadConf,
    }));
  };

  const handleEditLinesClick = () => {
    setLineEditorDialogVisibility(true);
  };

  useEffect(() => {
    if (currentConfig.identifiers) {
      delete currentConfig.identifiers;
    }
    onConfigChange(currentConfig);
  }, [currentConfig]);

  return (
    <div className="grid p-fluid">
      <div className="col-12 flex flex-column mb-3">
        <label htmlFor="identifiers">{T("word.datasets")}</label>
        <div className="flex gap-2">
          <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
          {selectedDatasets.length > 0 && (
            <>
              <Button
                icon="pi pi-pencil"
                type="button"
                className="p-button-secondary"
                tooltip={T("action.edit-lines")}
                onClick={handleEditLinesClick}
              />
              <Dialog
                onHide={() => setLineEditorDialogVisibility(false)}
                visible={lineEditorDialogVisible}
                header={T("action.bar.edit-bar-style")}
                style={{ width: "min(100vw, 1000px)", height: "400px" }}
              >
                <BarsIdentifiersEditor
                  currentConfig={currentConfig}
                  setCurrentConfig={setCurrentConfig}
                />
              </Dialog>
            </>
          )}
        </div>
      </div>
      <div className="col-2 flex flex-column mb-3">
        <label htmlFor="height">{T("word.height")}</label>
        <div className="p-inputgroup w-full">
          <InputNumber
            id="height"
            value={currentConfig.height as number}
            onValueChange={handleConfigChange}
          />
          <span className="p-inputgroup-addon">px</span>
        </div>
      </div>
      <div className="col-2 flex flex-column mb-3">
        <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
        <div className="p-inputgroup w-full">
          <InputNumber
            id="updateFrequency"
            value={currentConfig.updateFrequency as number}
            onValueChange={handleConfigChange}
          />
          <span className="p-inputgroup-addon">s</span>
        </div>
      </div>
      <div className="col-2 flex flex-column mb-full">
        <label htmlFor="timeAxisStepSeconds">{T("chart-config.time-axis-step")}</label>
        <div className="p-inputgroup w-full">
          <InputNumber
            id="timeAxisStepSeconds"
            value={currentConfig.timeAxisStepSeconds as number}
            onValueChange={handleConfigChange}
          />
          <span className="p-inputgroup-addon">s</span>
        </div>
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showDateSelection">{T("chart-config.show-date-selection")}</label>
        <InputSwitch
          id="showDateSelection"
          checked={currentConfig.showDateSelection}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="hideRefresh">{T("chart-config.hide-refresh")}</label>
        <InputSwitch
          id="hideRefresh"
          checked={currentConfig.hideRefresh}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="toggleTracesGrouping">{T("action.group-traces")}</label>
        <InputSwitch
          id="toggleTracesGrouping"
          checked={currentConfig.toggleTracesGrouping}
          onChange={handleConfigChange}
        />
      </div>
      <div className="gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="loadConf">{T("load-conf.load-conf")}</label>
        <InputSwitch
          id="loadConf"
          checked={currentConfig.loadConf !== undefined}
          onChange={handleLoadConf}
        />
      </div>
      {currentConfig.loadConf !== undefined && (
        <LoadConfigInputs
          loadConf={currentConfig.loadConf}
          handleLoadConfChange={handleLoadConfChange}
        />
      )}
    </div>
  );
}

export default CommonChartEditor;
